import { useState, useEffect, useRef, useCallback } from "react";
import { startGetContentRecommendations } from "../actions/user";
import { useDispatchApp } from "../lib/redux";
import { getIsVideoYoutube } from "../utils/string";
import {
  buildMediaUrlsSignature,
  doesMediaUrlsSignatureOnlyAddUrls,
  filterValidContentRecommendations,
  getContentRecommendationKey,
} from "../utils/mediaUrlsAnalysis";

export const useContentRecommendations = ({
  mediaUrls,
  platform,
  objective,
  campaignContext,
  disableAnalysis,
  validMediaUrls,
}) => {
  const dispatch = useDispatchApp();
  const mediaUrlsSignature = buildMediaUrlsSignature(mediaUrls);

  const [contentRecommendations, setContentRecommendations] = useState([]);
  const [isNoContentRecommendations, setIsNoContentRecommendations] =
    useState(false);
  const [isGettingContentRecommendations, setIsGettingContentRecommendations] =
    useState(false);
  const [contentRecommendationsLocked, setContentRecommendationsLocked] =
    useState(false);
  const [
    completedContentRecommendationKeys,
    setCompletedContentRecommendationKeys,
  ] = useState([]);

  const [isRecommendationsModalOpen, setIsRecommendationsModalOpen] =
    useState(false);
  const [createWithIAState, setCreateWithIAState] = useState(null);

  const recommendationPendingAfterSaveRef = useRef(null);
  const contentRecommendationsFetchedForSignatureRef = useRef(null);
  const lastMediaUrlsSignatureRef = useRef(null);
  const mediaUrlsSignatureRef = useRef(mediaUrlsSignature);

  useEffect(() => {
    mediaUrlsSignatureRef.current = mediaUrlsSignature;
  }, [mediaUrlsSignature]);

  const hasItemsAnalyzing = validMediaUrls.some(
    (mediaUrlData) => mediaUrlData?.isAnalyzing
  );
  const hasAtLeastOneCreativeNotYoutube = validMediaUrls.some(
    (mediaUrlData) => !getIsVideoYoutube(mediaUrlData?.url)
  );

  const onGetContentRecommendations = useCallback(async () => {
    if (!hasAtLeastOneCreativeNotYoutube) return;

    const signatureAtRequestStart = mediaUrlsSignatureRef.current;

    setIsGettingContentRecommendations(true);
    const response = await dispatch(
      startGetContentRecommendations({
        platform,
        contents: validMediaUrls,
        objective,
        campaignContext,
      })
    );

    const signatureStillMatches =
      signatureAtRequestStart === mediaUrlsSignatureRef.current;

    if (response?.ok && signatureStillMatches) {
      const filtered = filterValidContentRecommendations(
        response?.data?.recommendations
      );
      if (filtered.length > 0) {
        setContentRecommendations(filtered);
        setContentRecommendationsLocked(true);
      } else {
        setContentRecommendations([]);
        setIsNoContentRecommendations(true);
      }
    }

    if (signatureStillMatches) {
      contentRecommendationsFetchedForSignatureRef.current =
        signatureAtRequestStart;
    }
    setIsGettingContentRecommendations(false);
  }, [
    campaignContext,
    dispatch,
    hasAtLeastOneCreativeNotYoutube,
    objective,
    platform,
    validMediaUrls,
  ]);

  useEffect(() => {
    const previousSignature = lastMediaUrlsSignatureRef.current;
    lastMediaUrlsSignatureRef.current = mediaUrlsSignature;

    if (previousSignature === mediaUrlsSignature) return;

    if (
      previousSignature !== null &&
      doesMediaUrlsSignatureOnlyAddUrls({
        before: previousSignature,
        after: mediaUrlsSignature,
      })
    ) {
      return;
    }

    if (contentRecommendationsLocked) return;

    setContentRecommendations([]);
    setIsNoContentRecommendations(false);
    setCompletedContentRecommendationKeys([]);
    contentRecommendationsFetchedForSignatureRef.current = null;
  }, [contentRecommendationsLocked, mediaUrlsSignature]);

  useEffect(() => {
    if (disableAnalysis) return;
    if (contentRecommendationsLocked) return;

    const fetchedFor = contentRecommendationsFetchedForSignatureRef.current;
    if (
      fetchedFor &&
      (fetchedFor === mediaUrlsSignature ||
        doesMediaUrlsSignatureOnlyAddUrls({
          before: fetchedFor,
          after: mediaUrlsSignature,
        }))
    ) {
      return;
    }

    if (validMediaUrls.length === 0 || isGettingContentRecommendations) return;

    const hasAnalyzing = validMediaUrls.some((item) => item?.isAnalyzing);
    if (hasAnalyzing) return;

    const hasNonYoutube = validMediaUrls.some(
      (item) => !getIsVideoYoutube(item?.url)
    );
    if (!hasNonYoutube) return;

    const hasAnalysis = validMediaUrls.some((item) => item?.analysis);
    if (!hasAnalysis) return;

    onGetContentRecommendations();
  }, [
    contentRecommendationsLocked,
    disableAnalysis,
    isGettingContentRecommendations,
    mediaUrlsSignature,
    onGetContentRecommendations,
    validMediaUrls,
  ]);

  const isLoading = hasItemsAnalyzing || isGettingContentRecommendations;

  const showAnalyzingContentLoader =
    !disableAnalysis &&
    (isGettingContentRecommendations ||
      (!contentRecommendationsLocked && hasItemsAnalyzing));

  const showContentRecommendationsSummary =
    contentRecommendations.length > 0 &&
    !disableAnalysis &&
    (!isLoading || contentRecommendationsLocked);

  const onSelectContentRecommendation = useCallback(
    ({ mediaType, text }) => {
      recommendationPendingAfterSaveRef.current = { mediaType, text };
      setIsRecommendationsModalOpen(false);
      setCreateWithIAState({
        key: Date.now(),
        initialValues: {
          mediaType,
          prompt: text,
          platform,
          campaignContext,
          expandAdvancedConfigurationAccordion: true,
        },
      });
    },
    [campaignContext, platform]
  );

  const markRecommendationCompleted = useCallback((pending) => {
    const completionKey = getContentRecommendationKey(pending);
    setCompletedContentRecommendationKeys((previous) => {
      if (previous.includes(completionKey)) return previous;
      return [...previous, completionKey];
    });
  }, []);

  const buildWrappedCampaignMediaImport = useCallback(
    (onCallbackCampaignMediaImport) =>
      ({ entries }) => {
        onCallbackCampaignMediaImport?.({ entries });

        const pending = recommendationPendingAfterSaveRef.current;
        if (pending) {
          markRecommendationCompleted(pending);
          recommendationPendingAfterSaveRef.current = null;
        }
      },
    [markRecommendationCompleted]
  );

  const onCloseCreateWithIAModal = useCallback(() => {
    setCreateWithIAState(null);
    recommendationPendingAfterSaveRef.current = null;
  }, []);

  return {
    contentRecommendations,
    completedContentRecommendationKeys,
    isNoContentRecommendations,
    hasAtLeastOneCreativeNotYoutube,
    isRecommendationsModalOpen,
    setIsRecommendationsModalOpen,
    createWithIAState,
    onSelectContentRecommendation,
    onCloseCreateWithIAModal,
    buildWrappedCampaignMediaImport,
    showAnalyzingContentLoader,
    showContentRecommendationsSummary,
  };
};
